import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-015f1ee5"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "pagination" }
const _hoisted_2 = ["aria-label"]
const _hoisted_3 = ["disabled", "aria-label"]
const _hoisted_4 = ["disabled", "onClick", "aria-current", "aria-disabled", "aria-label"]
const _hoisted_5 = ["disabled", "aria-label"]
const _hoisted_6 = {
  class: "pagination__robot-links",
  "aria-hidden": "true"
}
const _hoisted_7 = {
  key: 0,
  class: "pagination__robot-link-item"
}
const _hoisted_8 = ["href"]
const _hoisted_9 = { class: "pagination__page-size" }
const _hoisted_10 = {
  "aria-hidden": "true",
  class: "pagination__page-size-label"
}
const _hoisted_11 = { class: "pagination-page-size-buttons" }
const _hoisted_12 = ["disabled", "onClick", "aria-label"]
const _hoisted_13 = {
  key: 1,
  class: "pagination__page-size-label"
}
const _hoisted_14 = ["aria-label", "value"]
const _hoisted_15 = ["value"]

import { useI18n } from "vue-i18n";
import { ref, computed, watch } from "vue";
import { useBreakpoints } from "@/code/useBreakpoints";
import dictionary from "@/i18n/dictionary.json";
import TressPaginationAnnouncer from "@/components/TressPaginationAnnouncer.vue";

type Props = {
  initialPage: number;
  initialTotalPages?: number;
  totalPages: number;
  currentPage?: number;
  currentPageSize?: number;
  listItemType?: string;
  listItemTypePlural?: string;
  pageSizeType?: string;
  pageSizeOptions?: number[];
};

type PageButton = { number: number; label: string; key: string; disabled: boolean };


export default /*#__PURE__*/_defineComponent({
  __name: 'TressPagination',
  props: {
    initialPage: {},
    initialTotalPages: { default: undefined },
    totalPages: {},
    currentPage: {},
    currentPageSize: { default: 20 },
    listItemType: { default: undefined },
    listItemTypePlural: { default: undefined },
    pageSizeType: { default: undefined },
    pageSizeOptions: { default: () => [20, 40, 60, 80] }
  },
  emits: ["pageChange", "pageSizeChange"],
  setup(__props: any, { emit }) {

const props = __props;

function buildRobotsPaginationLink(page: number): string {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);

  params.set("page", String(page));

  return `${url.origin}${url.pathname}?${params.toString()}`;
}

const { t } = useI18n();
const { isLg } = useBreakpoints();



const resolvedPageSizeType = props.pageSizeType || t(dictionary.pagination.listItemTypePluralGeneric);
const resolvedListItemTypePlural = props.listItemTypePlural || t(dictionary.pagination.listItemTypePluralGeneric);

const totalPagesNumber = ref(Number(props.initialTotalPages ?? props.totalPages));
const currentPageNumber = ref(Number(props.initialPage));
const currentPageSize = ref(Number(props.currentPageSize));

watch(
  () => props.totalPages,
  (newVal) => {
    if (newVal !== undefined) {
      totalPagesNumber.value = newVal;
    }
  },
  { immediate: true }
);
watch(
  () => props.currentPage,
  (newVal) => {
    if (newVal !== undefined) {
      currentPageNumber.value = newVal;
    }
  },
  { immediate: true }
);



const emitPageChange = (page: number) => {
  if (page !== currentPageNumber.value && page >= 1 && page <= totalPagesNumber.value) {
    currentPageNumber.value = page;
    emit("pageChange", page);
  }
};

const emitPageSizeChange = (size: number) => {
  if (size !== currentPageSize.value) {
    currentPageNumber.value = 1;
    currentPageSize.value = size;
    emit("pageSizeChange", size);
  }
};

const onPageSizeChange = (event: Event) => {
  const target = event.target as HTMLSelectElement | null;
  if (target) {
    emitPageSizeChange(Number(target.value));
  }
};

const pages = computed((): PageButton[] => {
  const result: PageButton[] = [];
  const total = totalPagesNumber.value;
  const current = currentPageNumber.value;
  const limit = isLg.value ? 5 : 3;

  const addPage = (n: number, label?: string) => {
    result.push({
      number: n,
      label: label ?? n.toString(),
      key: `page-${label ?? n}`,
      disabled: false,
    });
  };

  const addEllipsis = (key: string) => {
    result.push({
      number: -1,
      label: "…",
      key,
      disabled: true,
    });
  };

  // Show all pages if total fits within limit (no need for ellipsis or trimming)
  if (total <= limit) {
    for (let i = 1; i <= total; i++) {
      addPage(i);
    }
    return result;
  }

  // Exit early for compact layout
  if (limit === 3) {
    addPage(1);

    if (current === 1) {
      addPage(2);
    } else if (current === total) {
      addPage(total - 1);
    } else {
      addPage(current);
    }

    addPage(total);
    return result;
  }

  // Handle remaining pagination cases
  addPage(1); // always show first page
  let remaining = limit - 2; // reserve space for first and last page

  let showStartEllipsis = false;
  let showEndEllipsis = false;

  let start = Math.max(2, current - Math.floor(remaining / 2));
  let end = start + remaining - 1;

  if (end >= total) {
    end = total - 1;
    start = end - remaining + 1;
    if (start < 2) start = 2;
  }

  // Should we display ellipsis?
  if (start > 2) {
    showStartEllipsis = true;
    remaining -= 1;
    start += 1;
  }

  if (end < total - 1) {
    showEndEllipsis = true;
    remaining -= 1;
    end -= 1;
  }

  // Adjust again if room freed up
  while (end - start + 1 < remaining) {
    if (start > 2) start--;
    else if (end < total - 1) end++;
    else break;
  }

  if (showStartEllipsis) {
    addEllipsis("ellipsis-start");
  }

  for (let i = start; i <= end; i++) {
    addPage(i);
  }

  if (showEndEllipsis) {
    addEllipsis("ellipsis-end");
  }

  addPage(total); // always show last
  return result;
});

return (_ctx: any,_cache: any) => {
  const _component_icon_arrow_left = _resolveComponent("icon-arrow-left")!
  const _component_icon_arrow_right = _resolveComponent("icon-arrow-right")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(TressPaginationAnnouncer, {
      "current-page": currentPageNumber.value,
      "total-pages": totalPagesNumber.value
    }, null, 8, ["current-page", "total-pages"]),
    _createElementVNode("nav", {
      class: "pagination__nav",
      role: "navigation",
      "aria-label": _ctx.$t(_unref(dictionary).pagination.navigationFor, { listItemTypePlural: _unref(resolvedListItemTypePlural) })
    }, [
      _createElementVNode("button", {
        class: "pagination__button pagination__button--prev",
        disabled: currentPageNumber.value === 1,
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emitPageChange(currentPageNumber.value - 1))),
        "aria-label": _ctx.$t(_unref(dictionary).pagination.previousPage),
        type: "button"
      }, [
        _createVNode(_component_icon_arrow_left, { class: "pagination__arrow" })
      ], 8, _hoisted_3),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pages.value, (page) => {
        return (_openBlock(), _createElementBlock("button", {
          key: page.key,
          class: _normalizeClass(["pagination__button", {
          'pagination__button--active': page.number === currentPageNumber.value,
          'pagination__button--ellipsis': page.disabled,
        }]),
          disabled: page.disabled,
          onClick: ($event: any) => (!page.disabled && emitPageChange(page.number)),
          "aria-current": page.number === currentPageNumber.value ? 'page' : undefined,
          "aria-disabled": page.disabled ? 'true' : 'false',
          "aria-label": 
          page.number === -1
            ? _ctx.$t(_unref(dictionary).pagination.ellipsis)
            : _ctx.$t(_unref(dictionary).pagination.pageNumber, { page: page.number })
        ,
          type: "button"
        }, _toDisplayString(page.label), 11, _hoisted_4))
      }), 128)),
      _createElementVNode("button", {
        class: "pagination__button pagination__button--next",
        disabled: currentPageNumber.value === totalPagesNumber.value,
        onClick: _cache[1] || (_cache[1] = ($event: any) => (emitPageChange(currentPageNumber.value + 1))),
        "aria-label": _ctx.$t(_unref(dictionary).pagination.nextPage),
        type: "button"
      }, [
        _createVNode(_component_icon_arrow_right, { class: "pagination__arrow" })
      ], 8, _hoisted_5),
      _createElementVNode("ul", _hoisted_6, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pages.value, (page) => {
          return (_openBlock(), _createElementBlock(_Fragment, {
            key: `robot-link-${page.key}`
          }, [
            (page.number !== -1 && page.number !== currentPageNumber.value)
              ? (_openBlock(), _createElementBlock("li", _hoisted_7, [
                  _createElementVNode("a", {
                    class: "pagination__robot-link",
                    tabindex: "-1",
                    href: buildRobotsPaginationLink(page.number)
                  }, _toDisplayString(page.label), 9, _hoisted_8)
                ]))
              : _createCommentVNode("", true)
          ], 64))
        }), 128))
      ])
    ], 8, _hoisted_2),
    _createElementVNode("div", _hoisted_9, [
      (_unref(isLg))
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("span", _hoisted_10, _toDisplayString(_ctx.$t(_unref(dictionary).pagination.itemsPerPage, { type: _unref(resolvedPageSizeType) })) + ":", 1),
            _createElementVNode("div", _hoisted_11, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.pageSizeOptions, (size) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: size,
                  class: _normalizeClass(["pagination__page-size-button", { 'pagination__page-size-button--active': size === currentPageSize.value }]),
                  disabled: size === currentPageSize.value,
                  onClick: ($event: any) => (emitPageSizeChange(size)),
                  type: "button",
                  "aria-label": `${_ctx.$t(_unref(dictionary).pagination.itemsPerPage, { type: _unref(resolvedPageSizeType) })} ${size}`
                }, _toDisplayString(size), 11, _hoisted_12))
              }), 128))
            ])
          ], 64))
        : (_openBlock(), _createElementBlock("label", _hoisted_13, [
            _createTextVNode(_toDisplayString(_ctx.$t(_unref(dictionary).pagination.itemsPerPage, { type: _unref(resolvedPageSizeType) })) + ": ", 1),
            _createElementVNode("select", {
              name: "pagination-page-size",
              class: "pagination__page-size-select",
              "aria-label": _ctx.$t(_unref(dictionary).pagination.itemsPerPage, { type: _unref(resolvedPageSizeType) }),
              value: currentPageSize.value,
              onChange: onPageSizeChange
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.pageSizeOptions, (size, index) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: `${size}-${index}`,
                  value: size
                }, _toDisplayString(size), 9, _hoisted_15))
              }), 128))
            ], 40, _hoisted_14)
          ]))
    ])
  ]))
}
}

})